/**
 * THE ADMIN STUDIO — the curated superset, and the one manifest with an Admin Panel.
 *
 * <p>It is a SUPERSET of both products, and every id is written out rather than spread from the
 * registry. ⚠ Today those come to nearly the same list, so the distinction is about how it is
 * WRITTEN, not about its length: a tool added to the registry by a later stage reaches the Studio
 * only by somebody naming it here. A tool arriving by default is how the previous architecture
 * came to show sixty-three buttons.
 *
 * <p>The six unbuilt placeholder buttons are not in the registry at all — they carry no action
 * id, so nothing can name them, and the manifest-driven toolbar simply never mounts them.
 *
 * <h2>⚠ EXACTLY ONE HOME PER TOOL — the toolbar OR a panel tab, never both</h2>
 *
 * This file is the only manifest where that can go wrong, because it is the only one with a
 * panel. A tool in both places is two mount points for one control, and the two would eventually
 * disagree about state. {@code manifests.test.js} asserts the two sets are disjoint, and asserts
 * the union is REACHABLE — which is what catches the opposite mistake.
 *
 * <p>⚠ THAT SECOND HALF WAS NOT ASSERTED IN STAGE 1, AND SEVEN TOOLS WENT MISSING: the Φ Reversal
 * Scan, all four strategy designers, the Pivot Addition Series and the Fibonacci Pivot Pairs were
 * named in a panel TAB while the panel still rendered from {@code adminPanelTools.js}, which has
 * no descriptor for any of them. They were on neither surface — reachable from nowhere, with
 * nothing in any log. They are on the toolbar below, which is where they were before.
 */

import { CORE_DRAWING, CORE_INDICATORS, CLEAR_ACTIONS } from './astro.manifest';
import { TAB_CYCLES, TAB_GEOMETRY, TAB_SYSTEM } from '../adminPanelTools';

const VIEW_CHROME = Object.freeze([
  'chart_type',
  'area_zoom',
  'watermark_toggle',
  'save_screenshot',
  'copy_chart',
  'sweep_screenshots',
  'configuration',
]);

/** Every planetary study the astro product carries, plus the Visual Universe. */
const EPHEMERIS = Object.freeze([
  'planetary_lines',
  'average_longitude',
  'longitude_study',
  'declination_study',
  'degree_range_highlight',
  'area_highlight',
  'planet_follow',
  'planet_characteristics',
  'visual_universe',
  'astro_catalog_notes',
  'intraday_animation',
]);

/**
 * The scan pills the integralDelta product carries.
 *
 * <p>⚠ THE ORB SCANS ARE HERE AND THE ENGINE RUNNERS ARE IN TAB 1, which is not an inconsistency:
 * {@code cts_scan_1..3} mark timing points on the chart at three orbs, and {@code engine_cts} runs
 * the DynaMan engine over them. Different tools, different homes, and the brief's "Tab 1: … CTS,
 * PTA, PTB" names the runners.
 */
const QUANT = Object.freeze([
  'cts_scan_1',
  'cts_scan_2',
  'cts_scan_3',
  'pta_cycle',
  'ptb_cycle',
]);

/**
 * The research tools that live on the BAR rather than in the panel.
 *
 * <p>Their own block because this is the set Stage 1 orphaned — they were toolbar entries all
 * along, and naming them in a panel tab put them somewhere the panel could not render.
 */
const BAR_RESEARCH = Object.freeze([
  'spiral_reversal_scan',
  'strategy_1',
  'strategy_2',
  'time_event_strategy',
  'moa_strategy',
  'pivot_addition_series',
  'fib_pivot_pairs',
  'elliott_wave',
]);

export const adminManifest = Object.freeze({
  id: 'admin',

  brandName: 'integralDelta Studio [ADMIN]',

  /**
   * ⚠ `DEV / ADMIN`, so a screenshot cannot be mistaken for a customer's.
   *
   * The watermark is provenance on an exported image, and an operator looking at the superset
   * toolbar is not looking at either product. A brand name here would let an internal capture
   * circulate as though it came from the shipped app.
   */
  watermark: 'DEV / ADMIN',

  /**
   * ⚠ NO DOMAINS. Admin is reached by ROLE, never by hostname — `resolveInitialManifest` checks
   * the role before it looks at the host, so an administrator on `astrostockcharts.com` gets the
   * Studio. A domain here would hand the superset to every visitor of that host.
   */
  domains: Object.freeze([]),

  toolbar: Object.freeze([
    ...EPHEMERIS,
    ...QUANT,
    ...BAR_RESEARCH,
    ...CORE_DRAWING,
    'cycles_dialog',
    ...CORE_INDICATORS,
    ...VIEW_CHROME,
    ...CLEAR_ACTIONS,
    /* The panel's own entry point, which no customer manifest carries. */
    'admin_panel',
  ]),

  /**
   * THE PANEL, declared in full: tabs, their sub-headings, and the tools under each.
   *
   * <h2>⚠ THE GROUPING LIVES HERE, not on the tool descriptors</h2>
   *
   * {@code adminPanelTools.js} used to carry a {@code tab} and a {@code group} on every entry,
   * which made the panel's structure a property of twenty-five scattered objects. Membership and
   * layout are one decision — "what is on this tab, under what heading" — so they are made in one
   * place, and the descriptors keep only what is genuinely per-tool: the label, the blurb, and
   * whether the control needs a clicked bar.
   *
   * <p>A tool here with no descriptor still renders, from its registry label and with no blurb.
   * That is the honest degradation: a missing sentence is a thinner row, where a missing
   * descriptor used to be a tool that did not appear at all.
   */
  adminPanel: Object.freeze({
    tabs: Object.freeze([
      Object.freeze({
        id: TAB_CYCLES,
        label: 'Cycle & Confluence Studies',
        groups: Object.freeze([
          Object.freeze({
            label: 'Engines',
            tools: Object.freeze(['engine_pta', 'engine_ptb', 'engine_cts', 'portfolio_manager']),
          }),
          Object.freeze({
            label: 'TrendPulse',
            tools: Object.freeze(['trendpulse', 'pattern_prediction', 'validation_lines']),
          }),
          /* Both are self-contained COMPONENTS rendered through the panel's `slots` prop. */
          Object.freeze({
            label: 'Batch scans',
            tools: Object.freeze(['global_scan', 'auto_analysis']),
          }),
          Object.freeze({
            label: 'Cycle tools',
            tools: Object.freeze(['fixed_cycles']),
          }),
        ]),
      }),
      Object.freeze({
        id: TAB_GEOMETRY,
        label: 'Price & Geometric Overlays',
        groups: Object.freeze([
          Object.freeze({
            label: 'Fibonacci',
            tools: Object.freeze(['recent_pivot_fib']),
          }),
          Object.freeze({
            label: 'Gann geometry',
            tools: Object.freeze(['sk360', 'time_projection', 'sk_grid']),
          }),
        ]),
      }),
      Object.freeze({
        id: TAB_SYSTEM,
        label: 'System & Diagnostics',
        groups: Object.freeze([
          /*
           * ⚠ `messages_panel` AND `daily_scan_viewer` ARE NEW HERE, and their standalone role
           * gates in the toolbar are gone with them. They were `props.isAdmin &&` and nothing
           * else — reachable on the bar for an admin and invisible to the architecture, which is
           * the last place a control's home was decided outside a manifest.
           */
          Object.freeze({
            label: 'Chart tools',
            tools: Object.freeze([
              'message_centre', 'messages_panel', 'daily_scan_viewer', 'chart_comparison',
            ]),
          }),
          Object.freeze({
            label: 'Replay & simulation',
            tools: Object.freeze(['alpha_replay', 'dynaman_simulator', 'workbench']),
          }),
          Object.freeze({
            label: 'Audit & scans',
            tools: Object.freeze(['chart_analytica', 'market_scanner']),
          }),
        ]),
      }),
    ]),
  }),
});

export default adminManifest;
